import React from 'react';
import PropTypes from 'prop-types';
import { forgeClassHelper } from '../utils/classes';
import { debounce } from '../utils/helpers';

const classes = forgeClassHelper('textarea');

class Textarea extends React.Component {
  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.grow = this.grow.bind(this);
    this.shrink = this.shrink.bind(this);
    this.debouncedShrink = debounce(this.shrink, 100);

    this.state = {
      shrinking: false,
    };

    this.fieldRef = props.forwardedRef ? props.forwardedRef : React.createRef();
  }

  componentDidMount() {
    this.grow();

    window.addEventListener('resize', this.debouncedShrink);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.debouncedShrink);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.shrinking) {
      // If shrinking had no effect, just stop
      if (this.state.height === prevState.height) {
        this.setState({ shrinking: false });
      } else {
        // If the textarea needs to grow, it should stop shrinking
        const stopShrinking = this.grow();
        if (!stopShrinking) {
          this.shrink();
        }
      }
    }
  }

  onChange(event) {
    if (this.props.onChange) {
      this.props.onChange(event);
    }

    this.grow();
  }

  onBlur(event) {
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }

    this.shrink();
  }

  calcHeight(scrollHeight) {
    return `${scrollHeight + this.getBorderHeight() + 1}px`; // Add a pixel to safeguard against fractions
  }

  grow() {
    const scrollHeight = this.getScrollHeight();
    const height = this.getHeight();

    if (height < scrollHeight) {
      this.previousHeight = scrollHeight;
      this.setState({
        height: this.calcHeight(scrollHeight),
        shrinking: false,
      });
      return true;
    }
    return false;
  }

  /**
   * Iteratively shrink the textarea until the first active call to grow.
   */
  shrink() {
    // This doesn't need to be exact, it only impacts the number of iterations.
    const lineHeight = 20;

    const shorterHeight = this.getScrollHeight() - lineHeight;
    this.setState({
      height: this.calcHeight(shorterHeight),
      shrinking: true,
    });
  }

  /**
   * Stubbable methods for testing
   */
  getBorderHeight() {
    return this.fieldRef.current.offsetHeight - this.fieldRef.current.clientHeight;
  }

  getHeight() {
    return this.fieldRef.current.getBoundingClientRect().height;
  }

  getScrollHeight() {
    return this.fieldRef.current.scrollHeight;
  }
  /* End of stubbable methods */

  render() {
    const {
      error,
      disabled,
      className,
      style, // eslint-disable-line react/prop-types
      required,
      hideRequiredStyles,
      forwardedRef, // eslint-disable-line no-unused-vars
      ...fieldProps
    } = this.props;

    return (
      <textarea
        {...fieldProps}
        disabled={disabled}
        required={required}
        {...classes({
          modifiers: ['auto-size'],
          states: {
            disabled,
            error: error && !disabled,
            required: required && !hideRequiredStyles && !disabled,
          },
          extra: className,
        })}
        onChange={this.onChange}
        onBlur={this.onBlur}
        ref={this.fieldRef}
        style={{ ...style, height: this.state.height, overflow: this.state.shrinking && 'hidden' }}
      />
    );
  }
}

Textarea.propTypes = {
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Determines the initial value of the textarea, but allows it to be updated ([uncontrolled](https://reactjs.org/docs/uncontrolled-components.html)). */
  defaultValue: PropTypes.string,
  /** Impacts the textarea's appearance in addition to preventing editing. */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Hides any required styling. Can be set manually or by the required field variation set for `Form`. */
  hideRequiredStyles: PropTypes.bool,
  /** Function that takes a JS event as an argument. Called when the Textarea loses focus */
  onBlur: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the Textarea value changes */
  onChange: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the Textarea receives focus */
  onFocus: PropTypes.func,
  /** Ref forwarded to the input element */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    /** Note: { current: PropTypes.instanceOf(HTMLInputElement) } would be more
     * more correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  /** Indicates if it is a required field. Passed as an attribute to the native react `<textarea />` */
  required: PropTypes.bool,
  /** Value of the textarea. Creates a [controlled](https://reactjs.org/docs/forms.html#controlled-components) component. */
  value: PropTypes.string,
  /** Passthrough props to the `<textarea>` element */
  '...rest': PropTypes.any,
};

Textarea.defaultProps = {
  disabled: false,
  hideRequiredStyles: false,
  required: false,
};

// used for testing
export { Textarea as NoForwardRefTextarea };

function forwardRef(props, ref) {
  return <Textarea {...props} forwardedRef={ref} />;
}

forwardRef.displayName = 'Textarea';

export default React.forwardRef(forwardRef);
